/**
 * Conversations: the exchanges the team has with a contact, in one thread.
 *
 * **What the list holds, exactly.** A row is a stored text thread, or a contact the team has only
 * ever called — core-api derives the second kind at read time (`channel: "call"`), because
 * `conversations` is the texts' table and nothing about a call writes one (ADR-095). Before that,
 * a contact who was called and recorded but never texted was in no list at all, so there was no
 * way into their thread from this screen; the thread *body* has always drawn calls with their
 * recordings (`threadActivities`, off the record's timeline). An email thread (ADR-180) is a
 * conversation of its own channel, one per lead, with its own reply address: its emails are drawn
 * as one line each that opens the whole email, which only `GET /messages/{id}` carries. The other
 * channels this screen is shaped for, WhatsApp and iMessage, are not core-api's yet and appear only
 * on the development sample.
 *
 * Read from core-api (ADR-095 in `platform`): the inboxes and their conversations
 * (`textConversation`), a sender's texts (`messageActivity`), and a contact's thread from their
 * record's timeline (`threadActivities`). core-api keeps read and unread and the assignee; star,
 * followers, tags, owner, do-not-disturb and delete it does not keep, so they are offered only on
 * the development sample (`ORGANIZING_KEPT`), where the page holds them (`conversationsReducer`).
 * A reply leaves by SMS (ADR-098), by email (ADR-180), or as an internal comment, a note on the
 * record.
 *
 * The adapters that turn core-api's shapes into activities are `conversations-from-api.ts`, and
 * what a text costs to send is `sms-text.ts`; both are re-exported here, so a caller keeps one
 * import.
 *
 * Pure, for `test/conversations.test.ts` and `test/conversations-timeline.test.ts`.
 */

import { cadenceSender } from "@/lib/cadence";
import { scheduledFor, workflowSender } from "@/lib/texts";
import type {
  EmailCompose, LeadCallback, LeadNote, MessageCadence, SendingWorkflow, TextCompose, TextMedia,
} from "@/lib/types";
import { formatDateTime, formatPhoneNumber } from "@/lib/utils";

// What a text costs to send moved to its own module; re-exported so a caller that wants the
// composer's arithmetic still has one import.
export { smsBody, smsRoom, smsSegments } from "@/lib/sms-text";

/* --- the model ------------------------------------------------------------------------------ */

/** Where a message travelled. An internal comment never leaves the team. */
export type Channel = "sms" | "whatsapp" | "imessage" | "email" | "comment";
export type MessageChannel = Exclude<Channel, "comment">;
export type Direction = "inbound" | "outbound";
/** `received` is a text that came to us: core-api's own word for it. `scheduled` is one the system
 *  sent outside the contact's hours, waiting for their 8 am (ACL-630). */
export type MessageStatus = "scheduled" | "sending" | "sent" | "delivered" | "read" | "failed" | "received";
/** How a call ended, in the words the timeline uses. */
export type CallStatus = "completed" | "missed" | "busy" | "failed" | "no_answer";

export interface Person {
  id: string;
  name: string;
}

interface ActivityBase {
  id: string;
  /** ISO timestamp. */
  at: string;
}

export interface MessageActivity extends ActivityBase {
  kind: "message";
  channel: MessageChannel;
  direction: Direction;
  /** A text's words. For an email, its preview only: a list never carries a whole email, which is
   *  read from `GET /messages/{id}` when its line is opened. */
  body: string;
  /** The message's own id, for `GET /messages/{id}`; `id` is the timeline entry's key. "" on a
   *  conversation's last text as the list carries it, which is never opened. */
  messageId: string;
  /** The conversation it is in: a lead with an SMS and an email thread has both on their timeline,
   *  and each keeps its own read state. Null on the development sample. */
  conversationId: string | null;
  /** Email only. */
  subject?: string | null;
  /** Email only: the short name its one-line summary goes by, its subject or "Automatic reply". */
  label?: string | null;
  /** An email that arrived: whether it came from the contact's own address, vouched for by the
   *  sender's domain. Only then does its line name the contact. */
  fromContact?: boolean | null;
  /** An email that arrived: the address it came from, as this viewer may see it; null when hidden. */
  from?: string | null;
  /** Who on the team sent an outbound message; null for an automated one and for inbound. */
  author: Person | null;
  status: MessageStatus;
  /** Which of our numbers it was sent to, or from. */
  ourNumber?: string | null;
  /** The text was a STOP: what made the contact do-not-call. */
  optOut?: boolean;
  /** The pictures it carried (ADR-140), in the order sent. Each is fetched from the
   *  conversation's media route with the session's token. */
  media?: TextMedia[];
  /** Why a text we sent failed, as core-api says it for the provider's code. */
  error?: string | null;
  /** A scheduled text: when it goes out, the contact's 8 am (ACL-630). */
  sendAt?: string | null;
  /** The cadence step that sent it (ADR-135 in platform). Null on every other message. */
  cadence?: MessageCadence | null;
  /** The workflow that sent it (ACL-385); null on every other message. Named only to a reader who
   *  sees automation (ACL-591), so it is kept apart from `author`, which is always a person. */
  workflow?: SendingWorkflow | null;
}

/** A note for the team inside the thread. The contact never sees it. */
export interface CommentActivity extends ActivityBase {
  kind: "comment";
  /** What the note is: "Note", "Note with Callback", "Handoff brief" (`noteTitle`). */
  title?: string;
  body: string;
  author: Person;
  /** The record's note behind it, for editing and removing; null for one that is not on a record. */
  note: LeadNote | null;
}

/**
 * A call in the thread. The recording and transcript are not carried: `CallAudio` fetches them by
 * `callId` when someone asks, with the session's token (a plain `<audio src>` would get a 401).
 */
export interface CallActivity extends ActivityBase {
  kind: "call";
  direction: Direction;
  status: CallStatus;
  /** Talk time; null for a call that never connected, or a dial core-api does not time. */
  durationSeconds: number | null;
  /** The rep on the call. */
  agent: Person | null;
  /** The lead leg's channel uuid: what the recording and the transcript are keyed on. */
  callId: string | null;
  hasRecording: boolean;
  hasTranscript: boolean;
  /** The outcome the rep set, in the campaign's words. */
  outcome: string | null;
  /** How it went, in a line: "Talked 3:32 with Marcus Lee · Callback", "No answer · power dial". */
  summary: string;
  /** The call the dialer made for a callback, in a line (ADR-162 in `platform`): "Callback call ·
   *  No response · Signing". Null for any other call. */
  forCallback?: string | null;
}

/** Something that happened to the contact, told in the thread: an appointment, a stage. */
export interface EventActivity extends ActivityBase {
  kind: "event";
  text: string;
  /** A callback, as it was promised: the line draws its time on the lead's clock and where it
   *  stands now (`callbackWhen`, `callbackStatus`). */
  callback?: LeadCallback;
  /** The workflow that made the move (ACL-370), named after `text` only to a reader who sees
   *  automation (ACL-591). */
  workflow?: SendingWorkflow | null;
}

export type Activity = MessageActivity | CommentActivity | CallActivity | EventActivity;

/** Do-not-disturb, per way of reaching the contact. `all` stops every one of them. */
export const DND_CHANNELS = [
  { key: "all", label: "All channels" },
  { key: "calls", label: "Calls" },
  { key: "sms", label: "SMS" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "imessage", label: "iMessage" },
  { key: "email", label: "Email" },
] as const;
export type DndKey = (typeof DND_CHANNELS)[number]["key"];

export type FieldType = "text" | "date" | "phone" | "email";

/** One of the contact's own details, as the panel's read-only Contact folder shows it. */
export interface ContactField {
  key: string;
  label: string;
  value: string | null;
  type?: FieldType;
}

export interface ContactProfile {
  owner: Person | null;
  tags: string[];
  dnd: Record<DndKey, boolean>;
  /**
   * Who the contact is, for the panel's Contact folder: what they gave us (name, phone, email...) and
   * where the thread is, shown for context and never edited here; they are changed on the record. The
   * custom folders under it are the record's own (`GET /leads/{id}/fields`), not kept here.
   */
  details: ContactField[];
}

export interface Conversation {
  id: string;
  /** What kind of thread core-api keeps it as: texts, emails, or a contact only ever called. Null
   *  for a contact's own thread from their record (`lead:<id>`), and on the development sample. */
  channel: "sms" | "email" | "call" | null;
  /** The platform contact the thread is with, when the number matched one. */
  leadId: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  /** The teammate who owns the thread; null while nobody has picked it up. */
  assignee: Person | null;
  /** Teammates who asked to hear about it. */
  followers: Person[];
  starred: boolean;
  /** Activities nobody on the team has read yet. */
  unread: number;
  /** Deleted from the inbox, not from the record: the thread and everything in it are kept. */
  deletedAt: string | null;
  /** Oldest first. */
  activities: Activity[];
  contact: ContactProfile;
}

/* --- inboxes, tabs, sort and filter ---------------------------------------------------------- */

/**
 * Whether the thread's header should offer to call the contact (ACL-443).
 *
 * `DialNumberButton` falls back to plain text when there is nothing to dial, and as an icon that
 * would leave a bordered box answering no click. So the three conditions it checks are asked here
 * first: a number, an endpoint on this account to dial from, and a number that is really one.
 *
 * `dialable` is `isDialable` passed in, so this stays free of the softphone and can be asserted.
 */
export function canCallContact(
  phone: string | null | undefined,
  hasEndpoint: boolean,
  dialable: (n: string) => boolean,
): boolean {
  if (!phone || !hasEndpoint) return false;
  return dialable(phone);
}

export const INBOXES = [
  { id: "team", label: "Team Inbox", title: "Team Inbox", description: "Team-wide conversations" },
  { id: "mine", label: "All", title: "My Inbox", description: "Conversations assigned to you, and texts to your own number" },
  { id: "assigned", label: "Assigned to Me", title: "Assigned to Me", description: "Conversations assigned to you" },
] as const;
export type InboxId = (typeof INBOXES)[number]["id"];

export function inboxPath(id: InboxId): string {
  return `/conversations/${id}`;
}

export function isInboxId(value: string | undefined): value is InboxId {
  return INBOXES.some((i) => i.id === value);
}

export const INBOX_TABS = [
  { tab: "unread", label: "Unread" },
  { tab: "all", label: "All" },
  { tab: "recent", label: "Recent" },
  { tab: "starred", label: "Starred" },
] as const;
export type InboxTab = (typeof INBOX_TABS)[number]["tab"];

/** "Recent" is anything that moved in the last day. */
export const RECENT_MS = 24 * 60 * 60 * 1000;

export const SORTS = [
  { sort: "newest", label: "Newest first" },
  { sort: "oldest", label: "Oldest first" },
  { sort: "unread", label: "Unread first" },
] as const;
export type InboxSort = (typeof SORTS)[number]["sort"];

/** What a thread's latest activity was, for the list's filter and its channel mark. */
export type ActivityType = MessageChannel | "call" | "comment";

export const ACTIVITY_TYPES: { type: ActivityType; label: string }[] = [
  { type: "sms", label: "SMS" },
  { type: "whatsapp", label: "WhatsApp" },
  { type: "imessage", label: "iMessage" },
  { type: "email", label: "Email" },
  { type: "call", label: "Calls" },
  { type: "comment", label: "Internal comments" },
];

export interface InboxQuery {
  inbox: InboxId;
  tab: InboxTab;
  search: string;
  /** null keeps the order the list came in: core-api's, which sorts a paged list itself. */
  sort: InboxSort | null;
  /** Only threads whose latest activity is one of these; empty = every type. */
  types: ActivityType[];
  meId: string;
  now: number;
}

/**
 * Whether a thread the page holds is in an inbox. core-api's conversations come already narrowed
 * (`serverInbox`); this is for threads the page holds itself. "All" is Assigned to Me until
 * following is kept (ACL-335): core-api's `assigned` already takes texts to your own number.
 */
export function inInbox(c: Conversation, inbox: InboxId, meId: string): boolean {
  switch (inbox) {
    case "team":
      return true;
    case "mine":
    case "assigned":
      return c.assignee?.id === meId;
  }
}

/** The inbox as `GET /conversations?inbox=` asks for it. The list is paged, so the server narrows
 *  it, never the page. Team is everything you may read: no `inbox=`. */
export function serverInbox(inbox: InboxId): "assigned" | undefined {
  return inbox === "team" ? undefined : "assigned";
}

/**
 * Star, followers, tags, owner, do-not-disturb and delete are not kept by core-api: only read and
 * unread and the assignee are (ACL-335). On core-api's conversations they are not offered, since a
 * star gone after a reload reads as a bug. Which to keep is a ticket of its own.
 */
export const ORGANIZING_KEPT = false;

function inTab(c: Conversation, tab: InboxTab, now: number): boolean {
  switch (tab) {
    case "unread":
      return c.unread > 0;
    case "all":
      return true;
    case "recent": {
      const at = latestActivity(c)?.at;
      return at !== undefined && now - Date.parse(at) <= RECENT_MS;
    }
    case "starred":
      return c.starred;
  }
}

function matchesSearch(c: Conversation, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const digits = q.replace(/\D/g, "");
  if (digits.length >= 3 && (c.phone ?? "").replace(/\D/g, "").includes(digits)) return true;
  if ((c.contactName ?? "").toLowerCase().includes(q)) return true;
  if ((c.email ?? "").toLowerCase().includes(q)) return true;
  return c.activities.some(
    (a) => (a.kind === "message" || a.kind === "comment") && a.body.toLowerCase().includes(q),
  );
}

function lastAt(c: Conversation): number {
  const at = latestActivity(c)?.at;
  return at ? Date.parse(at) : 0;
}

/** The threads an inbox shows: its own, minus the deleted, through the tab, search and filter. */
export function visibleConversations(list: readonly Conversation[], q: InboxQuery): Conversation[] {
  const shown = list.filter(
    (c) =>
      c.deletedAt === null &&
      inInbox(c, q.inbox, q.meId) &&
      inTab(c, q.tab, q.now) &&
      matchesSearch(c, q.search) &&
      (q.types.length === 0 || q.types.includes(activityType(latestActivity(c)))),
  );
  if (q.sort === null) return shown;
  return shown.sort((a, b) => {
    if (q.sort === "unread" && (a.unread > 0) !== (b.unread > 0)) return a.unread > 0 ? -1 : 1;
    return q.sort === "oldest" ? lastAt(a) - lastAt(b) : lastAt(b) - lastAt(a);
  });
}

/** How many unread threads each tab's badge counts, within an inbox. */
export function unreadCount(list: readonly Conversation[], inbox: InboxId, meId: string): number {
  return list.filter((c) => c.deletedAt === null && c.unread > 0 && inInbox(c, inbox, meId)).length;
}

/* --- reading a thread ------------------------------------------------------------------------ */

/** Who the thread is with: the contact's name, else their number, else their email. */
export function conversationTitle(c: Pick<Conversation, "contactName" | "phone" | "email">): string {
  return c.contactName?.trim() || (c.phone ? formatPhoneNumber(c.phone) : null) || c.email || "Unknown contact";
}

/** The newest thing in the thread. An event (an appointment booked) is news too. */
export function latestActivity(c: Conversation): Activity | null {
  return c.activities.at(-1) ?? null;
}

export function activityType(a: Activity | null): ActivityType {
  if (!a) return "sms";
  if (a.kind === "message") return a.channel;
  if (a.kind === "call") return "call";
  if (a.kind === "comment") return "comment";
  return "sms";
}

export const CHANNEL_LABELS: Record<Channel, string> = {
  sms: "SMS",
  whatsapp: "WhatsApp",
  imessage: "iMessage",
  email: "Email",
  comment: "Internal comment",
};

/** Where a message stands, in the thread's words. */
export const MESSAGE_STATUS_LABELS: Record<MessageStatus, string> = {
  scheduled: "Scheduled",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Not delivered",
  received: "Received",
};

/**
 * What a message's time says under the pointer (ACL-591): the line under a bubble is the time alone
 * (and "Not delivered" on a text that failed), so the rest waits here — the channel and which of our
 * numbers, who sent one of ours, where it stands, and the full date. Who sent it is a person by name,
 * or for a text nobody sent by hand the workflow or cadence to a reader who `sees` automation and
 * "Automated" to anyone else.
 */
export function messageDetail(m: MessageActivity, sees: boolean): string {
  const out = m.direction === "outbound";
  const via = m.ourNumber
    ? `${CHANNEL_LABELS[m.channel]} ${out ? "from" : "to"} ${formatPhoneNumber(m.ourNumber)}`
    : CHANNEL_LABELS[m.channel];
  const by = !out
    ? null
    : m.author
      ? m.author.name
      : sees && m.workflow
        ? workflowSender(m.workflow)
        : sees && m.cadence
          ? cadenceSender(m.cadence)
          : "Automated";
  const stands = !out ? null : m.status === "scheduled" ? scheduledFor(m.sendAt) : MESSAGE_STATUS_LABELS[m.status];
  return [via, by, stands, formatDateTime(m.at)].filter(Boolean).join(" · ");
}

export const CALL_STATUS_LABELS: Record<CallStatus, string> = {
  completed: "Completed",
  missed: "Missed",
  busy: "Busy",
  failed: "Failed",
  no_answer: "No answer",
};

/** A call in a few words: "Inbound call · Missed", "Outbound call · 3:21". */
export function callSummary(a: CallActivity): string {
  const way = a.direction === "inbound" ? "Inbound call" : "Outbound call";
  if (a.status === "completed" && a.durationSeconds !== null) {
    return `${way} · ${formatSeconds(a.durationSeconds)}`;
  }
  return `${way} · ${CALL_STATUS_LABELS[a.status]}`;
}

/** How a call's card names its rep: who placed it, who took it, or — a caller nobody answered —
 *  whose phone it rang. "Answered by" on a missed call said the opposite of the badge beside it. */
export function callAgentVerb(a: Pick<CallActivity, "direction" | "status">): string {
  if (a.direction !== "inbound") return "By";
  return a.status === "completed" ? "Answered by" : "Rang";
}

/** One line for the list: what happened last, "You:" when the team did it. */
export function activityPreview(a: Activity | null): string {
  if (!a) return "No activity yet";
  const flat = (s: string) => s.replace(/\s+/g, " ").trim();
  switch (a.kind) {
    case "message": {
      const text = a.channel === "email" && a.subject ? a.subject : flat(a.body);
      return a.direction === "outbound" ? `You: ${text}` : text;
    }
    case "comment":
      return `Comment: ${flat(a.body)}`;
    case "call":
      return callSummary(a);
    case "event":
      return flat(a.text);
  }
}

/**
 * An email as its one line in a thread or on the record (ADR-180): "Email (Re: Your claim) sent to
 * Ali Khan on 23 Oct". The whole email is opened from the line, never drawn in the list.
 *
 * One that arrived names the contact only when core-api vouched it came from them (`fromContact`):
 * their own address, still theirs, and the sender's domain backing it. Anything else names the
 * address it really came from ("another address" when this viewer may not see it), with a note
 * that says why it is not the contact's. Whoever can send mail can put any name on it, so the
 * line never takes one on trust. `address` is the contact's address as this viewer sees it: with
 * it, a different sender is said to be one; without, only that it was not verified.
 */
export function emailLine(
  m: Pick<MessageActivity, "direction" | "label" | "subject" | "at" | "fromContact" | "from">,
  name: string,
  day: (iso: string) => string,
  address?: string | null,
): { text: string; note: string | null } {
  const what = `Email (${m.label?.trim() || m.subject?.trim() || "no subject"})`;
  const when = `on ${day(m.at)}`;
  if (m.direction === "outbound") return { text: `${what} sent to ${name} ${when}`, note: null };
  if (m.fromContact) return { text: `${what} received from ${name} ${when}`, note: null };
  const from = m.from?.trim() || null;
  const theirs = address?.trim().toLowerCase() || null;
  const elsewhere = from !== null && theirs !== null && from.toLowerCase() !== theirs;
  return {
    text: `${what} received from ${from ?? "another address"} ${when}`,
    note: elsewhere ? "not this contact's address" : "sender not verified",
  };
}

/** The thread's activities a header filter keeps. */
export type ActivityFilter = "all" | ActivityType;

export function filterActivities(list: readonly Activity[], filter: ActivityFilter): Activity[] {
  if (filter === "all") return [...list];
  // Events are the contact's own history and stay in every view: a filter narrows the conversation,
  // it does not hide that an appointment was booked.
  return list.filter((a) => a.kind === "event" || activityType(a) === filter);
}

/** `m:ss`, or `h:mm:ss` past an hour. */
export function formatSeconds(total: number): string {
  const s = Math.max(0, Math.floor(total));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** The contact's details a search finds, by label or value: all of them when it names the folder. */
export function searchDetails(details: readonly ContactField[], search: string): ContactField[] {
  const q = search.trim().toLowerCase();
  if (!q || "contact".includes(q)) return [...details];
  return details.filter((f) => f.label.toLowerCase().includes(q) || (f.value ?? "").toLowerCase().includes(q));
}

/* --- the page's own state, until the API holds it ------------------------------------------- */

export type ConversationAction =
  | { type: "load"; conversations: Conversation[] }
  | { type: "open"; id: string }
  | { type: "star"; id: string; starred: boolean }
  | { type: "read"; id: string; read: boolean }
  /** The unread count put back as it was: core-api refused a read or an unread the page showed. */
  | { type: "unread"; id: string; unread: number }
  | { type: "delete"; id: string; at: string }
  | { type: "restore"; id: string }
  | { type: "tag"; id: string; tag: string; on: boolean }
  | { type: "owner"; id: string; owner: Person | null }
  | { type: "follower"; id: string; person: Person; on: boolean }
  | { type: "dnd"; id: string; key: DndKey; on: boolean };

function update(list: Conversation[], id: string, change: (c: Conversation) => Conversation): Conversation[] {
  return list.map((c) => (c.id === id ? change(c) : c));
}

export function conversationsReducer(list: Conversation[], action: ConversationAction): Conversation[] {
  switch (action.type) {
    case "load":
      return mergeLoaded(list, action.conversations);
    case "open":
      // Opening a thread reads it.
      return update(list, action.id, (c) => (c.unread === 0 ? c : { ...c, unread: 0 }));
    case "star":
      return update(list, action.id, (c) => ({ ...c, starred: action.starred }));
    case "read":
      return update(list, action.id, (c) => ({ ...c, unread: action.read ? 0 : Math.max(1, c.unread) }));
    case "unread":
      return update(list, action.id, (c) => (c.unread === action.unread ? c : { ...c, unread: action.unread }));
    case "delete":
      // Out of every inbox, not out of the data: `deletedAt` hides it and `restore` brings it back.
      return update(list, action.id, (c) => ({ ...c, deletedAt: action.at }));
    case "restore":
      return update(list, action.id, (c) => ({ ...c, deletedAt: null }));
    case "tag": {
      const tag = action.tag.trim();
      if (!tag) return list;
      return update(list, action.id, (c) => {
        const has = c.contact.tags.some((t) => t.toLowerCase() === tag.toLowerCase());
        const tags = action.on
          ? has ? c.contact.tags : [...c.contact.tags, tag]
          : c.contact.tags.filter((t) => t.toLowerCase() !== tag.toLowerCase());
        return { ...c, contact: { ...c.contact, tags } };
      });
    }
    case "owner":
      return update(list, action.id, (c) => ({ ...c, contact: { ...c.contact, owner: action.owner } }));
    case "follower":
      return update(list, action.id, (c) => {
        const others = c.followers.filter((p) => p.id !== action.person.id);
        return { ...c, followers: action.on ? [...others, action.person] : others };
      });
    case "dnd":
      return update(list, action.id, (c) => ({
        ...c,
        contact: { ...c.contact, dnd: { ...c.contact.dnd, [action.key]: action.on } },
      }));
  }
}

/** Whether a channel is closed by do-not-disturb: its own switch, or All channels. */
export function dndOn(dnd: Record<DndKey, boolean>, key: DndKey): boolean {
  return dnd.all || dnd[key];
}

/**
 * Who can own or follow a thread, until the API lists the team: everyone the loaded threads name,
 * and the user, by name.
 */
export function teamMembers(list: readonly Conversation[], me: Person): Person[] {
  const byId = new Map<string, Person>([[me.id, me]]);
  for (const c of list) {
    for (const p of [c.assignee, c.contact.owner, ...c.followers]) if (p) byId.set(p.id, p);
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/* --- the composer ---------------------------------------------------------------------------- */

/** The composer's channels. SMS (ADR-098) and email (ADR-180) send wherever there is somewhere for
 *  them to go; WhatsApp and iMessage do not yet. A comment is a note on the contact's record, so it
 *  can be written wherever the thread has one (`composerReason`). */
export const COMPOSER_CHANNELS: { channel: Channel; label: string }[] = [
  { channel: "sms", label: "SMS" },
  { channel: "whatsapp", label: "WhatsApp" },
  { channel: "imessage", label: "iMessage" },
  { channel: "email", label: "Email" },
  { channel: "comment", label: "Internal comment" },
];

export const UNAVAILABLE_REASON = "Sending isn't connected yet.";
export const NO_RECORD_REASON = "Comments are kept on the contact's record, and this thread has none.";
/** Email on a thread with nowhere for it to go: no record to email, and not an email thread core-api
 *  would answer for itself (an Unassigned email thread says so in its own compose answer). */
export const NO_LEAD_EMAIL_REASON = "This thread is not on a contact's record, so it cannot be emailed from here.";

/**
 * Where an SMS from this thread goes (ADR-098): a reply in core-api's conversation, or a text to a
 * contact from their record when they have no conversation yet (`?lead=`) — the first one starts it.
 * Null where nothing can send: a thread the page holds itself.
 */
export type SmsTarget = { kind: "conversation"; id: string } | { kind: "lead"; leadId: string };

export function smsTarget(c: Pick<Conversation, "id" | "leadId"> & Partial<Pick<Conversation, "channel">>, live: boolean): SmsTarget | null {
  // An email thread is no number to text: a text from it goes to the contact from their record,
  // and joins or starts their own SMS thread.
  if (c.channel === "email") return c.leadId ? { kind: "lead", leadId: c.leadId } : null;
  if (c.id.startsWith("lead:")) return c.leadId ? { kind: "lead", leadId: c.leadId } : null;
  return live ? { kind: "conversation", id: c.id } : null;
}

/**
 * Where an email from this thread goes (ADR-180): a reply in core-api's email thread, or an email
 * to the contact from their record, which joins their email thread or starts it. Opened on an SMS
 * thread, an email goes to the record too: the two channels are two threads. Null where nothing can
 * send: a thread with no record that is not an email thread core-api has.
 */
export type EmailTarget = { kind: "conversation"; id: string } | { kind: "lead"; leadId: string };

export function emailTarget(
  c: Pick<Conversation, "id" | "leadId"> & Partial<Pick<Conversation, "channel">>,
  live: boolean,
): EmailTarget | null {
  if (live && c.channel === "email" && !c.id.startsWith("lead:")) return { kind: "conversation", id: c.id };
  return c.leadId ? { kind: "lead", leadId: c.leadId } : null;
}

/** Why the composer cannot send on `channel` in this thread, before anything about the contact
 *  (`blockedFor` in the composer) or what core-api says of this message (`composeRefusal`); null
 *  when it can. `target` is the chosen channel's: SMS and email send wherever there is somewhere for
 *  them to go, and WhatsApp and iMessage do not yet. */
export function composerReason(
  c: Pick<Conversation, "leadId">,
  channel: Channel,
  target: SmsTarget | EmailTarget | null = null,
): string | null {
  if (channel === "comment") return c.leadId ? null : NO_RECORD_REASON;
  if ((channel === "sms" || channel === "email") && target) return null;
  if (channel === "email" && !c.leadId) return NO_LEAD_EMAIL_REASON;
  return UNAVAILABLE_REASON;
}

/**
 * The channel a reply starts on. An email thread starts on email, and so does a thread the page was
 * opened on for an email (`prefer`, the record's Email button) where it has somewhere to go. Else
 * the contact's last (`defaultReplyChannel`), or a comment when that one cannot send and the thread
 * has a record to write it on. A contact's thread before they have a conversation starts on a
 * comment too: a text there starts one, and needs consent, so it is picked.
 */
export function initialComposerChannel(
  c: Conversation,
  smsTo: SmsTarget | null = null,
  emailTo: EmailTarget | null = null,
  prefer?: Channel,
): Channel {
  if (c.channel === "email" && emailTo) return "email";
  if (prefer === "email" && emailTo && (c.leadId || c.channel === "email")) return "email";
  if (smsTo?.kind === "lead" && c.leadId) return "comment";
  const channel = defaultReplyChannel(c);
  return composerReason(c, channel, channel === "email" ? emailTo : smsTo) && c.leadId ? "comment" : channel;
}

/**
 * Why core-api will not let a text or an email leave now, as its compose answer says it, with when
 * for quiet hours ("…on their own clock. Texts can go out from Tue 8:00 AM."); null when it may. An
 * email's answer has no quiet hours.
 */
export function composeRefusal(
  compose: Pick<TextCompose | EmailCompose, "can_send" | "detail"> & Partial<Pick<TextCompose, "until">>,
  when: (iso: string) => string = (iso) => new Date(iso).toLocaleString(),
): string | null {
  if (compose.can_send) return null;
  const detail = compose.detail ?? "A text cannot be sent to this number now.";
  return compose.until ? `${detail} Texts can go out from ${when(compose.until)}.` : detail;
}

/** Where a text stands, in the bubble's words: core-api's `queued` is ours, still sending. */
export function messageStatus(status: string, inbound: boolean): MessageStatus {
  if (inbound) return "received";
  switch (status) {
    case "scheduled":
      return "scheduled";
    case "queued":
      return "sending";
    case "delivered":
      return "delivered";
    case "failed":
      return "failed";
    default:
      return "sent";
  }
}

/** The channel a reply goes out on by default: the one the contact last wrote on, else SMS. */
export function defaultReplyChannel(c: Conversation): Channel {
  for (let i = c.activities.length - 1; i >= 0; i -= 1) {
    const a = c.activities[i]!;
    if (a.kind === "message" && a.direction === "inbound") return a.channel;
  }
  return c.phone ? "sms" : c.email ? "email" : "comment";
}

/* --- from core-api's shapes -------------------------------------------------------------------- */
//
// The adapters that turn a record's timeline and a conversation's texts into activities live in
// `conversations-from-api.ts`, and are re-exported here so a caller has one import for the model
// and the adapters alike.

export {
  activityOf, callStatus, messageActivity, recordConversation, RECORD_FILTERS,
  TEXT_FILTERS, textConversation, threadActivities, timelineKinds, type TimelineAsk,
} from "@/lib/conversations-from-api";

/**
 * A fresh load over what the page holds: core-api's facts (who, the last text, unread, assignee)
 * are taken as they come; what only the page keeps for now (star, deleted, followers, owner, tags,
 * do-not-disturb) survives the 30-second refetch for a conversation it already had.
 */
export function mergeLoaded(held: readonly Conversation[], loaded: readonly Conversation[]): Conversation[] {
  const byId = new Map(held.map((c) => [c.id, c]));
  return loaded.map((fresh) => {
    const had = byId.get(fresh.id);
    if (!had) return fresh;
    return {
      ...fresh,
      starred: had.starred,
      deletedAt: had.deletedAt,
      followers: had.followers,
      contact: { ...fresh.contact, owner: had.contact.owner, tags: had.contact.tags, dnd: had.contact.dnd },
    };
  });
}

/**
 * What was unread in a thread read from core-api when it was opened, taken before opening reads it:
 * how many texts, and when the conversation's newest text then arrived (`last_message_at`).
 */
export interface UnreadAtOpen {
  unread: number;
  /** The newest text when the thread was opened; null when the conversation did not say. */
  newest: string | null;
}

/**
 * Where the "New" line goes in a thread read from core-api: above the first of the texts the contact
 * sent that were unread when it was opened. They are counted back from the newest text there was
 * then, so a text that lands while the thread is open stays below the line instead of moving it.
 * Only texts are read or unread (a conversation's count, shared by its inbox); the rest of the
 * timeline keeps no read state.
 *
 * Read state is per conversation, and a contact's timeline holds every one of theirs: a lead with an
 * SMS and an email thread has both in it. With `conversationId`, only that conversation's messages
 * are counted, so the line sits above the opened thread's unread email and not above a text of the
 * contact's other thread.
 *
 * Undefined: no line. Null: the thread does not hold that newest text yet (a first page cached from
 * before it arrived), so the line cannot be placed until the thread loads it.
 */
export function firstNewText(
  activities: readonly Activity[],
  opened: UnreadAtOpen | undefined,
  conversationId?: string,
): string | undefined | null {
  if (!opened || opened.unread <= 0) return undefined;
  const parsed = opened.newest === null ? Number.NaN : Date.parse(opened.newest);
  const upTo = Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  const texts = activities.filter(
    (a): a is MessageActivity =>
      a.kind === "message" && (conversationId === undefined || a.conversationId === conversationId),
  );
  if (upTo !== Number.POSITIVE_INFINITY && !texts.some((a) => Date.parse(a.at) >= upTo)) return null;
  const received = texts.filter((a) => a.direction === "inbound" && Date.parse(a.at) <= upTo);
  return received[Math.max(0, received.length - opened.unread)]?.id;
}
